import type { InputTrack, InputTrackWarning } from '../../api'
import { t } from '../../i18n'
import { Icon, Note, Tag, TrackList, type TrackRow } from '../../ui'
import { byKind, codecName, endedFor, ignoredWhy, inputWarnings, ixOf, mainOf, multitrack, num, outputsOn, rate, vertical, type Sending } from '../tracks'

/** "3 V · 2 A": live tracks of each kind (strip cell, phone sheet header). */
export function trackCount(tracks: InputTrack[] | undefined): string {
  const live = (k: InputTrack['kind']) => byKind(tracks, k).filter((x) => x.state === 'live').length
  return t('tracks.count', { v: num(live('video')), a: num(live('audio')) })
}

/**
 * The tracks of the incoming stream in the Input panel and the phone Input sheet (design system
 * InputTracks): Video / Audio groups with counts, 40 px rows — index, what the eye checks, codec and
 * bitrate, tags and how many outputs use the track. Ignored (over the plan, codec) and ended tracks
 * say why in words, without colour. Shown only for a multitrack stream. `readOnly` (the console):
 * no output counts — the console does not know the user's choices.
 */
export function InputTracks({ tracks, warnings, sending, voice, limits, plan, now, readOnly, testId = 'input-tracks' }: {
  tracks?: InputTrack[]
  warnings?: InputTrackWarning[]
  /** what each output sends now (the «N out» count); absent in the console */
  sending?: Sending[]
  /** the stream key's voice_audio_track (null = A0) */
  voice?: number | null
  limits?: Record<string, number | string | null>
  plan?: string
  now: number
  readOnly?: boolean
  testId?: string
}) {
  if (!tracks || !multitrack(tracks)) return null
  const all = tracks
  const group = (kind: InputTrack['kind']) => {
    const list = byKind(all, kind)
    let ignored = 0
    let ended = 0
    const rows: TrackRow[] = list.map((tr) => {
      const ix = ixOf(tr.kind, tr.index)
      const why = ignoredWhy(tr, all, limits, plan)
      const gone = endedFor(tr, now)
      const state = why ? 'ignored' : gone !== undefined ? 'ended' : undefined
      if (state === 'ignored') ignored++
      if (state === 'ended') ended++
      const isVoice = kind === 'audio' && voice != null && voice === tr.index
      const tags = [vertical(tr) && <Tag key="v">9:16</Tag>, isVoice && <Tag key="voice">{t('tracks.tagVoice')}</Tag>].filter(Boolean)
      const uses = readOnly || state ? undefined : outputsOn(tr, sending)
      const sub = state === 'ignored'
        ? <><Icon name="state-disabled" size={12} />{why}</>
        : state === 'ended'
          ? <><Icon name="state-idle" size={12} />{t('tracks.ended', { s: num(gone!) })}</>
          : [kind === 'video' ? codecName(tr.codec) : '', tr.kbps ? rate(tr.kbps) : '', tr.index === 0 ? t('tracks.default') : '', isVoice ? t('tracks.captions') : ''].filter(Boolean).join(' · ')
      return {
        key: ix, ix, ixLabel: t(kind === 'video' ? 'tracks.ixVideo' : 'tracks.ixAudio', { n: tr.index }), state, testId: `track-${ix}`,
        main: mainOf(tr), sub,
        right: (tags.length || uses !== undefined) ? <>{tags.slice(0, 2)}{uses !== undefined && t('tracks.out', { n: num(uses) })}</> : undefined,
      }
    })
    const live = list.length - ignored - ended
    const extra = [ignored ? t('tracks.ignoredN', { n: num(ignored) }) : '', ended ? t('tracks.endedN', { n: num(ended) }) : ''].filter(Boolean)
    return { title: t(kind === 'video' ? 'tracks.video' : 'tracks.audio'), count: [num(live), ...extra].join(' + '), rows }
  }
  const warn = inputWarnings(warnings, limits)
  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <TrackList label={t('tracks.list')} groups={[group('video'), group('audio')]} />
      {/* DS: E1UI-01 — the plan's warnings about the tracks as a warn note under the list (no card for it yet) */}
      {warn.map((w) => <Note key={w.code} state="warn" data-testid={`track-warning-${w.code}`}>{w.text}</Note>)}
    </div>
  )
}
